import { useLocation } from 'react-router-dom';
import { usePermissions } from '../contexts/PermissionContext';
import {
  Home,
  CreditCard,
  Package,
  UserCheck,
  Users,
  ShoppingCart,
  Calculator,
  BarChart3,
  Settings,
  HardDrive,
  Database,
  CloudUpload,
  CloudDownload
} from 'lucide-react';

export const useNavigation = () => {
  const location = useLocation();
  const { hasPermission, hasAnyPermission } = usePermissions();

  // All navigation items with permission requirements
  const allNavigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      active: location.pathname === '/dashboard' || location.pathname === '/',
      permission: 'dashboard.view'
    },
    {
      name: 'POS',
      icon: CreditCard,
      children: [
        { 
          name: 'Retail POS', 
          href: '/retail-pos', 
          active: location.pathname === '/retail-pos',
          permission: 'pos.retail'
        },
        { 
          name: 'Bulk Sales', 
          href: '/bulk-sales', 
          active: location.pathname === '/bulk-sales',
          permission: 'pos.bulk'
        },
        { 
          name: 'Proforma Invoice', 
          href: '/proforma-invoice', 
          active: location.pathname === '/proforma-invoice',
          permission: 'pos.proforma'
        },
        { 
          name: 'Credit Note', 
          href: '/credit-note', 
          active: location.pathname === '/credit-note',
          permission: 'pos.credit'
        }
      ],
      permission: 'pos.view'
    },
    {
      name: 'Products',
      icon: Package,
      children: [
        { 
          name: 'Product List', 
          href: '/products', 
          active: location.pathname === '/products',
          permission: 'products.view'
        },
        { 
          name: 'Categories', 
          href: '/categories', 
          active: location.pathname === '/categories',
          permission: 'products.view'
        },
        { 
          name: 'Stock Management', 
          href: '/stock', 
          active: location.pathname === '/stock',
          permission: 'inventory.view'
        }
      ],
      permission: 'products.view'
    },
    {
      name: 'Customers',
      icon: UserCheck,
      children: [
        { 
          name: 'Customer List', 
          href: '/customers', 
          active: location.pathname === '/customers',
          permission: 'users.view'
        },
        { 
          name: 'Customer Payments', 
          href: '/customer-payments', 
          active: location.pathname === '/customer-payments',
          permission: 'users.view'
        }
      ],
      permission: 'users.view'
    },
    {
      name: 'Suppliers',
      icon: Users,
      children: [
        { 
          name: 'Supplier List', 
          href: '/suppliers', 
          active: location.pathname === '/suppliers',
          permission: 'suppliers.view'
        },
        { 
          name: 'Supplier Payments', 
          href: '/supplier-payments', 
          active: location.pathname === '/supplier-payments',
          permission: 'suppliers.manage'
        }
      ],
      permission: 'suppliers.view'
    },
    {
      name: 'Purchases',
      icon: ShoppingCart,
      children: [
        { 
          name: 'Purchase List', 
          href: '/purchases', 
          active: location.pathname === '/purchases',
          permission: 'inventory.view'
        },
        { 
          name: 'Return Purchases', 
          href: '/purchases/return', 
          active: location.pathname === '/purchases/return',
          permission: 'inventory.stock'
        },
        { 
          name: 'Receive Purchases', 
          href: '/purchases/receive', 
          active: location.pathname === '/purchases/receive',
          permission: 'inventory.stock'
        }
      ],
      permission: 'inventory.view'
    },
    {
      name: 'Accounts',
      icon: Calculator,
      children: [
        { 
          name: 'Expenses', 
          href: '/expenses', 
          active: location.pathname === '/expenses',
          permission: 'expenses.view'
        },
        { 
          name: 'Cash Analysis', 
          href: '/cash-analysis', 
          active: location.pathname === '/cash-analysis',
          permission: 'cash.view'
        }
      ],
      permission: 'cash.view'
    },
    {
      name: 'Reports',
      icon: BarChart3,
      children: [
        { 
          name: 'Sales Report', 
          href: '/sales-report', 
          active: location.pathname === '/sales-report',
          permission: 'reports.sales'
        },
        { 
          name: 'Inventory Report', 
          href: '/inventory-report', 
          active: location.pathname === '/inventory-report',
          permission: 'reports.inventory'
        },
        { 
          name: 'Financial Report', 
          href: '/financial-report', 
          active: location.pathname === '/financial-report',
          permission: 'reports.financial'
        }
      ],
      permission: 'reports.sales'
    },
    {
      name: 'System Management',
      icon: HardDrive,
      children: [
        { 
          name: 'Backup & Restore', 
          href: '/system/backup', 
          active: location.pathname === '/system/backup',
          permission: 'system.backup'
        },
        { 
          name: 'Data Management', 
          href: '/system/data',
          active: location.pathname === '/system/data',
          permission: 'system.export'
        },
        {
          name: 'System Logs',
          href: '/system/logs',
          active: location.pathname === '/system/logs',
          permission: 'system.logs'
        }
      ],
      permission: 'system.view'
    },
    {
      name: 'Settings',
      icon: Settings,
      children: [
        { 
          name: 'General Settings', 
          href: '/settings', 
          active: location.pathname === '/settings',
          permission: 'settings.view'
        },
        { 
          name: 'User Management', 
          href: '/users', 
          active: location.pathname === '/users',
          permission: 'users.view'
        },
        { 
          name: 'Roles & Permissions', 
          href: '/role-permissions', 
          active: location.pathname === '/role-permissions',
          permission: 'roles.manage'
        }
      ],
      permission: 'settings.view'
    }
  ];

  // Filter navigation items based on user permissions
  const filterNavigationItems = (items) => {
    return items.filter(item => {
      // Check if user has permission for this item
      if (item.permission && !hasPermission(item.permission)) {
        return false;
      }

      // If item has children, filter them too
      if (item.children) {
        const filteredChildren = item.children.filter(child => {
          return !child.permission || hasPermission(child.permission);
        });
        
        // Only show parent if it has visible children
        if (filteredChildren.length === 0) {
          return false;
        }
        
        // Update the item with filtered children
        item.children = filteredChildren;
      }

      return true;
    });
  };

  const navigationItems = filterNavigationItems(allNavigationItems);

  return {
    navigationItems,
    allNavigationItems
  };
};
